/**
 * CompleteProfileComponent - Mandatory Profile Completion
 *
 * Displayed after phone authentication when user profile is incomplete.
 * User must fill in their name before accessing the application.
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { safeInternalReturnUrl } from '../../../core/utils/return-url.util';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { Auth, updateEmail } from '@angular/fire/auth';
import { UserProfile } from '../../../core/models/user-profile';
import { AuthService } from '../../../core/services/auth.service';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'app-complete-profile',
    templateUrl: './complete-profile.component.html',
    styleUrls: ['./complete-profile.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, SharedModule]
})
export class CompleteProfileComponent implements OnInit {
  profileForm!: FormGroup;
  isLoading = false;
  errorMessage = '';

  private returnUrl: string = '/';
  private userId: string | null = null;
  private userPhone: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private firestore: Firestore,
    private auth: Auth,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.captureQueryParams();
    this.checkAuthState();
  }

  private initializeForm(): void {
    this.profileForm = this.fb.group({
      fullname: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      // Email is REQUIRED for phone-only users to enable account recovery
      email: ['', [Validators.required, Validators.email]]
    });
  }

  private captureQueryParams(): void {
    this.returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl'], '/');
    this.userPhone = this.route.snapshot.queryParams['phone'] || null;
  }

  private async checkAuthState(): Promise<void> {
    const user = this.auth.currentUser;

    if (!user) {
      // Not authenticated, redirect to auth
      this.router.navigate(['/auth']);
      return;
    }

    this.userId = user.uid;
    this.userPhone = user.phoneNumber || this.userPhone;

    // Check if profile already complete
    // 🎯 #331-B3/B4 : complet = fullname ET email (aligné sur createPhoneProfile).
    // Un profil avec fullname mais sans email doit repasser ici pour renseigner
    // l'email (sinon paiement bloqué → EMAIL_REQUIRED).
    const userDoc = await getDoc(doc(this.firestore, 'users', user.uid));
    if (userDoc.exists()) {
      const profile = userDoc.data() as UserProfile;
      const hasFullname = !!(profile.fullname && profile.fullname.trim() !== '');
      const hasEmail = !!(profile.email && profile.email.trim() !== '');
      if (hasFullname && hasEmail) {
        // Profile already complete, redirect
        this.router.navigateByUrl(this.returnUrl);
        return;
      }
      // Pre-fill known fields
      if (profile.fullname) {
        this.profileForm.patchValue({ fullname: profile.fullname });
      }
      if (profile.email) {
        this.profileForm.patchValue({ email: profile.email });
      }
    }
  }

  async onSubmit(): Promise<void> {
    if (this.profileForm.invalid || this.isLoading || !this.userId) {
      this.markFormTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const formData = this.profileForm.value;
      const email = (formData.email || '').trim();

      // 🎯 #331-B4 (fix de fond) : lier l'email au compte Firebase Auth pour
      // qu'il soit peuplé nativement sur le token (`user.email`) — les comptes
      // phone-auth ont sinon `email === null` et le paiement dépend du fallback
      // serveur users/{uid}. Tenté AVANT l'écriture Firestore : un email déjà
      // pris par un autre compte doit bloquer (unicité), pas être enregistré.
      const authUser = this.auth.currentUser;
      if (authUser && !authUser.email && email) {
        try {
          await updateEmail(authUser, email);
          console.log('✅ [CompleteProfile] Email lié au compte Auth');
        } catch (linkError: any) {
          if (linkError?.code === 'auth/email-already-in-use') {
            this.errorMessage = 'AUTH.COMPLETE_PROFILE.ERRORS.EMAIL_IN_USE';
            this.isLoading = false;
            return;
          }
          // requires-recent-login / operation-not-allowed (email enumeration
          // protection) : non bloquant — le profil Firestore suffit, le
          // fallback serveur users/{uid} couvre le paiement (#331-A).
          console.warn('⚠️ [CompleteProfile] Lien email→Auth impossible, profil Firestore seul:', linkError?.code);
        }
      }

      // Update or create user profile
      const profileUpdate: Partial<UserProfile> = {
        uid: this.userId,
        fullname: formData.fullname.trim(),
        phoneNumber: this.userPhone || undefined
      };

      // Add email if provided
      if (email) {
        profileUpdate.email = email;
      }

      await setDoc(doc(this.firestore, 'users', this.userId), profileUpdate, { merge: true });

      console.log('✅ [CompleteProfile] Profile updated successfully');

      // Refresh the user profile in AuthService to notify all subscribers (e.g., main-header)
      await this.authService.refreshUserProfile();
      console.log('🔄 [CompleteProfile] Profile refreshed in AuthService');

      // Navigate to return URL
      this.router.navigateByUrl(this.returnUrl);

    } catch (error: any) {
      console.error('❌ [CompleteProfile] Failed to update profile:', error);
      this.errorMessage = 'AUTH.COMPLETE_PROFILE.ERRORS.SAVE_FAILED';
      this.isLoading = false;
    }
  }

  private markFormTouched(): void {
    Object.keys(this.profileForm.controls).forEach(key => {
      this.profileForm.get(key)?.markAsTouched();
    });
  }

  // Template helpers
  get fullnameControl() {
    return this.profileForm.get('fullname');
  }

  get emailControl() {
    return this.profileForm.get('email');
  }

  get displayPhone(): string {
    return this.userPhone || '';
  }
}
